import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { normalize, schema } from 'normalizr';
import { combineReducers } from 'redux';
import {
    ApiException,
    CalculationDto,
    CalculationResult,
    CostDto,
    CostGroupDto,
    CostTypeDto,
    CreateCalculationRequest,
    CreateCostGroupRequest,
    CreateCostRequest,
    PricelistPeriodDto,
    RounderDto,
    SimpleCalculationDto,
    UpdateCalculationDto
} from '../../../common/PricePrgClient';
import { createAsyncAction } from '../../../utils';
import { CostTypeDropped } from '../calculationTypes';

const namespaces = {
    calculation: 'calculation',
    costTypes: 'costTypes',
    showHideCostGroupDialog: 'showHideCostGroupDialog',
    isCalculationsLoading: 'isCalculationsLoading',
    calculationBasePrice: 'calculationBasePrice',
    selectedCalculationPricelist: 'selectedCalculationPricelist',
    editingCostGroup: 'editingCostGroup',
    isEditCostGroupDialogVisible: 'isEditCostGroupDialogVisible',
    isDeleteCostDialogVisible: 'isDeleteCostDialogVisible',
    showCreateNewCalculationDialog: 'showCreateNewCalculationDialog',
    showCreateNewCostDialog: 'showCreateNewCostDialog',
    executedCalculationResult: 'executedCalculationResult',
    isEditingCost: 'isEditingCost',
    isEditCostDialogVisible: 'isEditCostDialogVisible',
    isEditCostDialogLoading: 'isEditCostDialogLoading',
    allCalculations: 'allCalculations',
    productSearchResult: 'productSearchResult',
    calculationSelectedProduct: 'calculationSelectedProduct',
    showCreateNewCalculationButton: 'showCreateNewCalculationButton',
    showEditNameInput: 'showEditNameInput',
    isEditNameLoading: 'isEditNameLoading',
    rounders: 'rounders',
    isRecalculateDialogVisible: 'isRecalculateDialogVisible',
    isRecalculatingPrices: 'isRecalculatingPrices',
    pricelistPeriodDates: 'pricelistPeriodDatess'
};
const initialCalculation: CalculationDto = {
    calculationId: 0,
    name: '',
    pricelistRef: undefined,
    pricelistId: 0,
    rounder: '',
    costGroups: [],
    inheritedFrom: undefined
};
export const fetchCalculation = createAsyncAction<number, CalculationDto, ApiException>(
    namespaces.calculation,
    'fetchCalculation'
);
export const updateCalculation = createAsyncAction<UpdateCalculationDto, UpdateCalculationDto>(
    namespaces.calculation,
    'updateCalculation'
);
const calculationSlice = createSlice({
    name: namespaces.calculation,
    initialState: initialCalculation,
    reducers: {},
    extraReducers: builder =>
        builder
            .addCase(fetchCalculation.success, (_, action) => action.payload)
            .addCase(fetchCalculation.failure, () => initialCalculation)
            .addCase(updateCalculation.success, (state, action) => {
                state.name = action.payload.name ?? '';
                state.rounder = action.payload.rounder;
            })
});

export const fetchCostTypes = createAsyncAction<void, CostTypeDto[]>(
    namespaces.costTypes,
    'updateCalculation'
);

const costTypesSlice = createSlice({
    name: namespaces.costTypes,
    initialState: [] as CostTypeDto[],
    reducers: {},
    extraReducers: builder => builder.addCase(fetchCostTypes.success, (state, action) => action.payload)
});

type CreateCostGroupRequestPayload = {
    pricelistId: number;
    costGroupDto: CreateCostGroupRequest;
};
type DeleteCostGroupRequest = { costGroupId: number };

export const createCostGroup = createAsyncAction<CreateCostGroupRequestPayload, void>(
    namespaces.showHideCostGroupDialog,
    'createCostGroup'
);
export const deleteCostGroup = createAsyncAction<DeleteCostGroupRequest, void>(
    namespaces.showHideCostGroupDialog,
    'deleteCostGroup'
);
const showHideCostGroupDialogSlice = createSlice({
    name: namespaces.showHideCostGroupDialog,
    initialState: false,
    reducers: {
        showNewCostGroupDialog: () => true,
        hideNewCostGroupDialog: () => false
    },
    extraReducers: builder => builder.addCase(createCostGroup.success, () => false)
});
export const { hideNewCostGroupDialog, showNewCostGroupDialog } = showHideCostGroupDialogSlice.actions;

type ReorderCostRequest = { costId: number; order: number };
export const reorderCost = createAsyncAction<ReorderCostRequest, void, ApiException>(
    namespaces.isCalculationsLoading,
    'reorderCost'
);

const isLoadingSlice = createSlice({
    name: namespaces.isCalculationsLoading,
    initialState: true,
    reducers: {},
    extraReducers: builder =>
        builder
            .addCase(fetchCalculation.request, () => true)
            .addCase(fetchCalculation.success, () => false)
            .addCase(fetchCalculation.failure, () => false)
            .addCase(reorderCost.request, () => true)
            .addCase(reorderCost.success, () => false)
            .addCase(reorderCost.failure, () => false)
});

const basePriceSlice = createSlice({
    name: namespaces.calculationBasePrice,
    initialState: 0,
    reducers: {
        setBasePrice: (state, action: PayloadAction<number>) => action.payload
    }
});
export const { setBasePrice } = basePriceSlice.actions;

const selectedCalculationPricelistSlice = createSlice({
    name: namespaces.selectedCalculationPricelist,
    initialState: 0,
    reducers: {
        selectCalculationPricelist: (state, action: PayloadAction<number>) => action.payload
    }
});
export const { selectCalculationPricelist } = selectedCalculationPricelistSlice.actions;

export const updateCostGroup = createAsyncAction<CostGroupDto, void>(
    namespaces.editingCostGroup,
    'updateCostGroup'
);
const editingCostGroupSlice = createSlice({
    name: namespaces.editingCostGroup,
    initialState: null as CostGroupDto | null,
    reducers: {
        showEditCostGroupDialog: (state, action: PayloadAction<CostGroupDto | null>) => action.payload
    },
    extraReducers: builder => builder.addCase(updateCostGroup.success, () => null)
});
export const { showEditCostGroupDialog } = editingCostGroupSlice.actions;

const isEditCostGroupDialogVisibleSlice = createSlice({
    name: namespaces.isEditCostGroupDialogVisible,
    initialState: false,
    reducers: {},
    extraReducers: builder =>
        builder
            .addCase(updateCostGroup.success, () => false)
            .addCase(showEditCostGroupDialog, (state, action) => action.payload != null)
});

type DeleteCostRequest = { costId: number };
export const deleteCost = createAsyncAction<DeleteCostRequest, void>(
    namespaces.isDeleteCostDialogVisible,
    'deleteCost'
);
const isDeleteCostDialogVisibleSlice = createSlice({
    name: namespaces.isDeleteCostDialogVisible,
    initialState: null as number | null,
    reducers: {
        showDeleteCostDialog: (state, action: PayloadAction<number | null>) => action.payload,
        deleteCostCancelled: () => null
    },
    extraReducers: builder => builder.addCase(deleteCost.success, () => null)
});
export const { showDeleteCostDialog, deleteCostCancelled } = isDeleteCostDialogVisibleSlice.actions;

type CreateCalculationSuccess = CreateCalculationRequest;
export const createCalculation = createAsyncAction<
    CreateCalculationRequest,
    CreateCalculationSuccess,
    ApiException
>(namespaces.showCreateNewCalculationDialog, 'createCalculation');

const showCreateNewCalculationDialogSlice = createSlice({
    name: namespaces.showCreateNewCalculationDialog,
    initialState: false,
    reducers: {
        showNewCalculationDialog: (state, action: PayloadAction<boolean>) => action.payload
    },
    extraReducers: builder => builder.addCase(createCalculation.success, () => false)
});

export const { showNewCalculationDialog } = showCreateNewCalculationDialogSlice.actions;

const showCreateNewCostDialogSlice = createSlice({
    name: namespaces.showCreateNewCostDialog,
    initialState: {
        costTypeId: undefined,
        costGroupId: undefined,
        showDialog: false
    } as CostTypeDropped,
    reducers: {
        showNewCostDialog: (state, action: PayloadAction<CostTypeDropped>) => ({
            costTypeId: action.payload.costTypeId,
            costGroupId: action.payload.costGroupId,
            showDialog: action.payload.showDialog
        })
    }
});
export const { showNewCostDialog } = showCreateNewCostDialogSlice.actions;

type CalculateCalculationRequest = {
    pricelistId: number;
    basePrice: number;
    productId: number;
};
export const calculateCalculation = createAsyncAction<CalculateCalculationRequest, CalculationResult>(
    namespaces.executedCalculationResult,
    'calculateCalculation'
);

const executedCalculationResultSlice = createSlice({
    name: namespaces.executedCalculationResult,
    initialState: null as CalculationResult | null,
    reducers: {},
    extraReducers: builder =>
        builder
            .addCase(calculateCalculation.success, (state, action) => action.payload)
            .addCase(fetchCalculation.success, () => null)
});

type CreateCostRequestPayload = {
    costGroupId: number;
    createCostRequest: CreateCostRequest;
};

export const updateCost = createAsyncAction<CostDto, void>(namespaces.isEditingCost, 'updateCost');
export const createCost = createAsyncAction<CreateCostRequestPayload, void>(
    namespaces.isEditingCost,
    'createCost'
);

const isEditingCostSlice = createSlice({
    name: namespaces.isEditingCost,
    initialState: null as CostDto | null,
    reducers: {
        showEditCostDialog: (state, action: PayloadAction<CostDto | null>) => action.payload
    },
    extraReducers: builder =>
        builder.addCase(updateCost.success, () => null).addCase(createCost.success, () => null)
});
export const { showEditCostDialog } = isEditingCostSlice.actions;

const isEditCostDialogVisibleSlice = createSlice({
    name: namespaces.isEditCostDialogVisible,
    initialState: false,
    reducers: {},
    extraReducers: builder =>
        builder
            .addCase(showEditCostDialog, (state, action) => action.payload != null)
            .addCase(updateCost.success, () => false)
            .addCase(createCost.success, () => false)
});

const isEditCostDialogLoadingSlice = createSlice({
    name: namespaces.isEditCostDialogLoading,
    initialState: true,
    reducers: {},
    extraReducers: builder =>
        builder
            .addCase(showEditCostDialog, (_, action) => action.payload == null)
            .addCase(showNewCostDialog, (_, action) => action.payload == null)
            .addCase(updateCost.request, () => true)
            .addCase(createCost.request, () => true)
            .addCase(updateCost.success, () => false)
            .addCase(createCost.success, () => false)
});

export type ProductSearchResult = {
    name: string;
    productId: number;
};

const caltulationEntity = new schema.Entity<SimpleCalculationDto>('calculations', undefined, {
    idAttribute: 'calculationId'
});

export const fetchCalculations = createAsyncAction<void, SimpleCalculationDto[]>(
    namespaces.allCalculations,
    'fetchCalculations'
);

const allCalculationsSlice = createSlice({
    name: namespaces.allCalculations,
    initialState: {} as { [calculationId: number]: SimpleCalculationDto },
    reducers: {},
    extraReducers: builder =>
        builder.addCase(fetchCalculations.success, (_, action) => {
            const normalized = normalize(action.payload, [caltulationEntity]);
            return normalized.entities.calculations ?? {};
        })
});

export const searchProduct = createAsyncAction<string | undefined | null, ProductSearchResult[]>(
    namespaces.productSearchResult,
    'searchProduct'
);

const productSearchResultSlice = createSlice({
    name: namespaces.productSearchResult,
    initialState: [] as ProductSearchResult[],
    reducers: {},
    extraReducers: builder => builder.addCase(searchProduct.success, (state, action) => action.payload)
});

const selectedProductSlice = createSlice({
    name: namespaces.calculationSelectedProduct,
    initialState: null as ProductSearchResult | null,
    reducers: {
        selectProduct: (state, action: PayloadAction<ProductSearchResult | null>) => action.payload
    }
});
export const { selectProduct } = selectedProductSlice.actions;

const showCreateNewCalculationButtonSlice = createSlice({
    name: namespaces.showCreateNewCalculationButton,
    initialState: false,
    reducers: {},
    extraReducers: builder =>
        builder
            .addCase(fetchCalculation.failure, (_, action) => action.payload.status === 404)
            .addCase(createCalculation.success, () => false)
            .addCase(fetchCalculation.success, () => false)
            .addCase(createCalculation.failure, () => true)
});

const showEditNameInputSlice = createSlice({
    name: namespaces.showEditNameInput,
    initialState: false,
    reducers: {
        showEditNameInput: (state, action: PayloadAction<boolean>) => action.payload
    },
    extraReducers: builder =>
        builder
            .addCase(updateCalculation.success, () => false)
            .addCase(fetchCalculations.success, () => false)
});
export const { showEditNameInput: isEditingName } = showEditNameInputSlice.actions;

const isEditNameLoadingSlice = createSlice({
    name: namespaces.isEditNameLoading,
    initialState: false,
    reducers: {},
    extraReducers: builder =>
        builder //
            .addCase(updateCalculation.success, () => false)
            .addCase(updateCalculation.request, () => true)
});

export const fetchRounders = createAsyncAction<void, RounderDto[]>(namespaces.rounders, 'fetchRounders');

const roundersSlice = createSlice({
    name: namespaces.rounders,
    initialState: [] as RounderDto[],
    reducers: {},
    extraReducers: builder =>
        builder //
            .addCase(fetchRounders.success, (_, action) => action.payload)
});
type RecalculatePricesRequest = {
    pricelistId: number;
    fromDate: string;
};
export const recalculatePrices = createAsyncAction<RecalculatePricesRequest, boolean>(
    namespaces.isRecalculatingPrices,
    'recalculatePrices'
);

const isRecalculateDialogVisibleSlice = createSlice({
    name: namespaces.isRecalculateDialogVisible,
    initialState: false,
    reducers: { showRecalculateDialog: (_, action) => action.payload },
    extraReducers: builder => builder.addCase(recalculatePrices.request, () => false)
});

const isRecalculatingPricesSlice = createSlice({
    name: namespaces.isRecalculatingPrices,
    initialState: false,
    reducers: {},
    extraReducers: builder =>
        builder //
            .addCase(recalculatePrices.request, () => true)
            .addCase(recalculatePrices.success, () => false)
});
export const { showRecalculateDialog } = isRecalculateDialogVisibleSlice.actions;

export const fetchPricelistPeriods = createAsyncAction<number[], PricelistPeriodDto[]>(
    namespaces.pricelistPeriodDates,
    'fetchPricelistPeriods'
);

const pricelistPeriodDatesSlice = createSlice({
    name: namespaces.pricelistPeriodDates,
    initialState: {
        dates: [] as string[],
        isLoading: true
    },
    reducers: { isLoading: (state, action) => action.payload },
    extraReducers: builder =>
        builder
            .addCase(fetchPricelistPeriods.success, (state, action) => {
                state.dates = action.payload.map(c => c.validFrom);
                state.isLoading = false;
            })
            .addCase(fetchPricelistPeriods.request, state => {
                state.isLoading = true;
            })
});

export default combineReducers({
    calculation: calculationSlice.reducer,
    isLoading: isLoadingSlice.reducer,
    costtypes: costTypesSlice.reducer,
    showHideCostGroupDialog: showHideCostGroupDialogSlice.reducer,
    basePrice: basePriceSlice.reducer,
    showCreateNewCalculationButton: showCreateNewCalculationButtonSlice.reducer,
    selectedCalculationPricelist: selectedCalculationPricelistSlice.reducer,
    showCreateNewCalculationDialog: showCreateNewCalculationDialogSlice.reducer,
    showCreateNewCostDialog: showCreateNewCostDialogSlice.reducer,
    productSearchResult: productSearchResultSlice.reducer,
    selectedProduct: selectedProductSlice.reducer,
    isDeleteCostDialogVisible: isDeleteCostDialogVisibleSlice.reducer,
    isEditingCost: isEditingCostSlice.reducer,
    isEditCostDialogVisible: isEditCostDialogVisibleSlice.reducer,
    isEditCostGroupDialogVisible: isEditCostGroupDialogVisibleSlice.reducer,
    isEditCostDialogLoading: isEditCostDialogLoadingSlice.reducer,
    editingCostGroup: editingCostGroupSlice.reducer,
    executedCalculationResult: executedCalculationResultSlice.reducer,
    rounders: roundersSlice.reducer,
    isRecalculatingPrices: isRecalculatingPricesSlice.reducer,
    showEditNameInput: showEditNameInputSlice.reducer,
    isEditNameLoading: isEditNameLoadingSlice.reducer,
    calculations: allCalculationsSlice.reducer,
    showRecalculateDialog: isRecalculateDialogVisibleSlice.reducer,
    pricelistPeriodDates: pricelistPeriodDatesSlice.reducer
});
